import { SKIN_CATALOG } from '../assets/skinCatalog';
import type { AssetManifest } from '../assets/catalog';

const urls = new Map<string, string>();
const pending = new Map<string, Promise<void>>();
let manifest: AssetManifest | null = null;
// Relative to the page's base so a build under a sub-path (e.g. --base ./) finds its art.
// Absolute, because url() inside a CSS variable resolves against the stylesheet that uses it.
const ASSETS = new URL(`${import.meta.env.BASE_URL}assets/`, document.baseURI).href;
export const skinKey = (path: string): string => path.replaceAll('/', '-');
export const skinUrl = (id: string): string | undefined => urls.get(id.startsWith('skin:') ? id : `skin:${id}`);

export function decorateSkinTitles(root: HTMLElement): void {
  const titles = [['.nb-page-title .crayon-title','my-notebook'], ['.bt-title-text','battle'], ['.creator-top .crayon-title','create-share']];
  for (const [selector, name] of titles) {
    const el = root.querySelector<HTMLElement>(selector!);
    const url = skinUrl(`title/${name}`);
    if (!el || !url || root.classList.contains('creator-hero-mode') || el.querySelector('.skin-title-art')) continue;
    el.classList.add('skin-title','has-title');
    const label = el.getAttribute('aria-label') ?? el.textContent ?? '';
    el.setAttribute('aria-label',label);
    // Wrap text nodes too: the original styled Thai title stays in the DOM.
    const fallback = document.createElement('span');
    while (el.firstChild) fallback.appendChild(el.firstChild);
    el.appendChild(fallback);
    const img = new Image(); img.className = 'skin-title-art'; img.alt = ''; img.setAttribute('aria-hidden','true'); img.src = url;
    el.appendChild(img);
  }
}

/** No requests for absent manifest entries, and never fall back to PNG masters. */
export async function initSkin(): Promise<void> {
  const overlay = document.createElement('div');
  overlay.className = 'skin-light'; overlay.setAttribute('aria-hidden','true');
  for (const cls of ['warm','vignette','grain']) { const i = document.createElement('i'); i.className = cls; overlay.appendChild(i); }
  document.body.appendChild(overlay);
  try {
    const response = await fetch(`${ASSETS}manifest.json`);
    if (response.ok) manifest = await response.json() as AssetManifest;
  } catch { /* The procedural notebook works offline without a manifest. */ }
}

async function load(path: string): Promise<void> {
  if (pending.has(path)) return pending.get(path);
  const job = (async () => {
    const id = `skin:${path}`;
    const master = manifest?.assets[id]?.states.idle?.[0];
    const file = master && (manifest?.runtime?.[master]?.file ?? (master.endsWith('.webp') ? master : undefined));
    if (!file?.startsWith('skin/') || !file.endsWith('.webp') || file.includes('..')) return;
    const url = `${ASSETS}${file}`;
    const img = new Image();
    img.src = url;
    try { await img.decode(); } catch { return; }
    urls.set(id, url);
    const key = skinKey(path);
    document.documentElement.style.setProperty(`--skin-${key}`, `url("${url}")`);
    document.documentElement.classList.add(`has-skin-${key}`, 'skin-ready');
  })();
  pending.set(path, job);
  return job;
}

/** Decode the incoming screen's pieces before its page turn; share the browser cache. */
export async function preloadSkin(screen: string): Promise<void> {
  if (!['menu','notebook','battle','creator'].includes(screen)) return;
  const paths = SKIN_CATALOG.map(s => s.id.slice(5)).filter(path => {
    if (path.startsWith('scenery/')) return path === `scenery/${screen}`;
    if (path.startsWith('title/')) return path === `title/${({ menu: 'paper-legends', notebook: 'my-notebook', battle: 'battle', creator: 'create-share' } as Record<string,string>)[screen]}`;
    if (path.startsWith('notebook/')) return path === `notebook/${screen === 'notebook' ? 'spread' : 'single'}`;
    if (path.startsWith('rps/')) return screen === 'battle';
    return true;
  });
  await Promise.all(paths.map(load));
}
